import React, { useState, useEffect, useMemo } from 'react';
import acc from 'accounting';
import cls from 'classname';

import { Row, Col, Divider, Input, InputNumber, Slider } from 'antd';

import Icon from '../../../Icon';
import ProductDetailsCard from './ProductDetailsCard';

import './NumbersBreakdownBlock.less';

const calculator = {
  getFees: (price) => (acc.unformat(price, ',') / 100) * 2.9 + 0.3,
  getCPA: (price) => acc.unformat(price, ',') / 3,
  getRatio: ({ price, cost, shipping }) => acc.unformat(price, ',') / (cost + shipping),
  getBECPA: ({ price, cost, shipping, fees }) => acc.unformat(price, ',') - cost - shipping - fees,
  getBEROAS: ({ price, BECPA }) => acc.unformat(price, ',') / BECPA,
  getMargin: ({ price, CPA, BECPA }) => ((BECPA - CPA) / acc.unformat(price, ',')) * 100,
  getProfit: ({ orders, BECPA, CPA }) => orders * (BECPA - CPA),
};

const fieldList = [
  {
    id: 1,
    name: 'cost',
    label: 'Est. Cost of Good Sold',
    text: 'The amount you expect to pay your supplier for each unit.'
  },
  {
    id: 2,
    name: 'shipping',
    label: 'Est. Shipping Cost',
    text: 'The amount you expect to pay your supplier for each unit shipped.'
  },
  {
    id: 3,
    name: 'fees',
    label: 'Est. Fees Per Order',
    text: 'The estimated fee you expect to pay your payment processor per order. Calculated as 2.9% + $0.3 per order.'
  },
  {
    id: 4,
    name: 'CPA',
    label: 'Est. CPA',
    text: 'The amount you expect to spend for a conversion. It is calculated as a third of “Selling Price'
  },
];

export const NumbersBreakdownBlock = ({
  skeleton,
  isMobile,
  theme,
  product,
}) => {
  const [state, setState] = useState({
    price: 24.99,
    orders: 25000,
    cost: 4.95,
    shipping: 0.72,
    fees: (24.99 / 100) * 2.9 + 0.3,
    CPA: 24.99 / 3,
  });
  const [isChanged, setIsChanged] = useState(true);


  const defaultValue = {
    price: 24.99,
    orders: 25000,
    cost: 4.95,
    shipping: 0.72,
    fees: 0.3,
    CPA: 11,
  };

  useEffect(() => {
    let fees = 0.3;
    let CPA = 11;
    if (state.price !== defaultValue.price) {
      const parsedPrice = acc.unformat(state.price, ',');
      fees = calculator.getFees(parsedPrice);
      CPA = calculator.getCPA(parsedPrice);
    }
    setState((state) => ({ ...state, fees, CPA }));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.price]);

  useEffect(() => {
    saveDefaultValue();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    checkIsChanged();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    state.price,
    state.orders,
    state.cost,
    state.shipping,
    state.fees,
    state.CPA,
  ]);

  function saveDefaultValue() {
    setState((state) => ({ ...state, ...defaultValue }));
  }

  const memoRatio = useMemo(() => {
    if (!acc.unformat(state.price, ',')) return 0;
    return calculator.getRatio({
      price: state.price,
      cost: state.cost,
      shipping: state.shipping,
    });
  }, [state.price, state.cost, state.shipping]);

  const memoBECPA = useMemo(
    () =>
      calculator.getBECPA({
        price: state.price,
        cost: state.cost,
        shipping: state.shipping,
        fees: state.fees,
      }),
    [state.price, state.cost, state.shipping, state.fees],
  );

  const memoBEROAS = useMemo(
    () =>
      calculator.getBEROAS({
        price: state.price,
        BECPA: memoBECPA,
      }),
    [state.price, memoBECPA],
  );

  const memoMargin = useMemo(() => {
    if (!acc.unformat(state.price, ',')) return 0;
    return calculator.getMargin({
      price: state.price,
      CPA: state.CPA,
      BECPA: memoBECPA,
    });
  }, [state.price, state.CPA, memoBECPA]);

  const memoProfit = useMemo(
    () =>
      calculator.getProfit({
        orders: state.orders,
        BECPA: memoBECPA,
        CPA: state.CPA,
      }),
    [state.orders, state.CPA, memoBECPA],
  );

  function handleChange(event) {
    const { name, value } = event.target;
    const parsedValue = value.replace(/\$\s?|(,*)/g, '');

    const reg = /^-?\d*(\.\d*)?$/;
    if ((!isNaN(parsedValue) && reg.test(parsedValue)) || parsedValue === '') {
      if (name === 'CPA' && Number(parsedValue) > 100) return;
      setState((state) => ({ ...state, [name]: parsedValue }));
    }
  }

  function handleChangeNumber(value, name) {
    if (acc.formatNumber(value, 2, '', ',') === acc.formatNumber(state[name], 2, '', ',')) return
    setState((state) => ({ ...state, [name]: +value }));
  }

  function checkIsChanged() {
    if (
      Object.keys(defaultValue).some((key) => defaultValue[key] !== state[key])
    ) {
      if (!isChanged) setIsChanged(true);
    } else if (isChanged) setIsChanged(false);
  }

  function handleResetValue() {
    saveDefaultValue();
  }

  const renderHeader = (
    <>
      <span className="title">Numbers Breakdown</span>

      <span
        className={cls('link', { disabled: !isChanged })}
        onClick={handleResetValue}
      >
        Reset
      </span>
    </>
  );

  const renderHeaderSkeleton = (
    <>
      <span className="title skeleton" style={{ width: 222 }} />
      <span className="link skeleton" style={{ width: 44 }} />
    </>
  );

  const renderTooltipIcon = (text, className) => (
    <Icon
      type="attention_outline"
      role="button"
      titleText={text}
      tooltipProps={{
        placement: 'top',
        destroyTooltipOnHide: true,
        trigger: isMobile ? 'click' : 'hover',
        overlayClassName: cls('details-tooltip', className),
        getPopupContainer: () =>
          document.getElementById('product-details-block-numbers-id'),
      }}
    />
  );

  const marksPrice = {
    0: '$0',
    500: '$500+',
  };

  const marksOrders = {
    0: '0',
    100000: '100,000+',
  };

  if (skeleton) {
    return (
      <ProductDetailsCard header={renderHeaderSkeleton} withLine={!isMobile}>
        <Row gutter={[{ xs: 12, sm: 32, xl: 16 }, 22]}>
          {[1, 2].map((el) => (
            <Col key={el} xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 8 }}>
              <div className="numbers-breakdown-card">
                <span
                  className="numbers-breakdown-card-label skeleton"
                  style={{ width: 115 }}
                />
                <div className="numbers-breakdown-card-input skeleton" />
                <div className="numbers-breakdown-card-slider numbers-breakdown-card-slider_skeleton">
                  <div className="rail skeleton" />
                  <span className="text skeleton" />
                  <span className="text skeleton" style={{ width: 56 }} />
                </div>
              </div>
            </Col>
          ))}
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            lg={{ span: 8 }}
            className="numbers-breakdown-card-wrap pot_profit"
          >
            <div className="numbers-breakdown-card">
              <span
                className="numbers-breakdown-card-label skeleton"
                style={{ width: 102 }}
              />
              <span className="numbers-breakdown-card-result skeleton" />
            </div>
          </Col>
        </Row>
        <Divider style={{ margin: '16px 0' }} />
        <Row
          gutter={[
            { xs: 12, sm: 12, lg: 16 },
            { xs: 16, sm: 16, lg: 32 },
          ]}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8].map((el) => (
            <Col key={el} xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
              <div className="numbers-breakdown-card">
                <span
                  className="numbers-breakdown-card-label small skeleton"
                  style={{ width: 104 }}
                />
                <div
                  className="numbers-breakdown-card-input skeleton"
                  style={{ maxWidth: 164 }}
                />
              </div>
            </Col>
          ))}
        </Row>
      </ProductDetailsCard>
    );
  }
  return (
    <ProductDetailsCard header={renderHeader} withLine={!isMobile}>
      <Row gutter={[{ xs: 12, sm: 32, xl: 16 }, 22]}>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 8 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label">
              Selling Price
              {renderTooltipIcon('This is the price you plan on charging your customers.', 'price')}
            </span>

            <InputNumber
              className={cls('numbers-breakdown-card-input selling-price', {
                dark: theme === 'dark',
              })}
              name="price"
              value={state.price}
              step={0.01}
              decimalSeparator={','}
              precision={2}
              onChange={(value) => handleChangeNumber(value, 'price')}
              stringMode
            />
            <Slider
              className="numbers-breakdown-card-slider"
              value={acc.unformat(state['price'], ',')}
              max={500}
              marks={marksPrice}
              tooltipVisible={false}
              onChange={(value) => handleChangeNumber(value, 'price')}
              onAfterChange={(value) =>
                setState((state) => ({
                  ...state,
                  price: acc.formatNumber(value, 2, '', ','),
                }))
              }
            />
          </div>
        </Col>

        <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 8 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label">
              Example Orders
              {renderTooltipIcon('The amount of units you expect to sell. This is the default value we put across all products.', 'orders')}
            </span>
            <Input
              className="numbers-breakdown-card-input"
              prefix={
                <>
                  <Icon role="icon" type="orders" />
                  <div className="divider" />
                </>
              }
              name="orders"
              value={acc.formatNumber(state.orders, 0, ',', '')}
              onChange={handleChange}
            />

            <Slider
              className="numbers-breakdown-card-slider"
              defaultValue={25000}
              value={acc.unformat(state.orders)}
              // value={state.orders}
              max={100000}
              marks={marksOrders}
              tooltipVisible={false}
              onChange={(value) => handleChangeNumber(value, 'orders')}
            />
          </div>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          lg={{ span: 8 }}
          className="numbers-breakdown-card-wrap pot_profit"
        >
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label">
              POT. Profit
              {renderTooltipIcon('The potential profit is calculated by subtracting BECPA with the CPA of the product, and the difference is multiplied by 25,000 to give you an idea of the potential profit if you sold that many units.', 'pot_profit')}
            </span>
            <span className="numbers-breakdown-card-result">
              {`≈ $${acc.formatNumber(memoProfit, 0, ',')}`}
            </span>
          </div>
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      <Row
        gutter={[
          { xs: 12, sm: 12, lg: 16 },
          { xs: 16, sm: 16, lg: 32 },
        ]}
        // gutter={[16, 32]}
      >
        {fieldList.map((el) => (
          <Col key={el.id} xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
            <div className="numbers-breakdown-card">
              <span className="numbers-breakdown-card-label small">
                {el.label}
                {renderTooltipIcon(el.text, el.name)}
              </span>
              <InputNumber
                className={cls('numbers-breakdown-card-input', {
                  dark: theme === 'dark',
                })}
                name={el.name}
                value={state[el.name]}
                step={0.01}
                decimalSeparator={','}
                precision={2}
                onChange={(value) => handleChangeNumber(value, el.name)}
                stringMode
              />
            </div>
          </Col>
        ))}

        {/* result fields */}

        <Col xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label small">
              P/C Ratio
              {renderTooltipIcon('The price to cost ratio is calculated by dividing the product cost with the selling price.', 'p_c_ratio')}
            </span>
            <Input
              className="numbers-breakdown-card-input"
              name="p_c_ratio"
              value={`${acc.formatNumber(memoRatio, 2, ',')}X`}
              disabled
            />
          </div>
        </Col>

        <Col xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label small">
              BECPA
              {renderTooltipIcon('The breakeven cost per acquisition (BECPA) is the maximum CPA you should aim to get to breakeven on the product.', 'BECPA')}
            </span>
            <Input
              className="numbers-breakdown-card-input"
              name="BECPA"
              value={`$${acc.formatNumber(memoBECPA, 2, '', ',')}`}
              disabled
            />
          </div>
        </Col>

        <Col xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label small">
              BEROAS
              {renderTooltipIcon('The breakeven return on ad spend (BEROAS) is the minimum ROAS you should aim to get to breakeven on the product.', 'BEROAS')}
            </span>
            <Input
              className="numbers-breakdown-card-input"
              name="BEROAS"
              value={acc.formatNumber(memoBEROAS, 2, ',')}
              disabled
            />
          </div>
        </Col>

        <Col xs={{ span: 12 }} sm={{ span: 8 }} lg={{ span: 6 }}>
          <div className="numbers-breakdown-card">
            <span className="numbers-breakdown-card-label small">
              Profit Margin
              {renderTooltipIcon('This is the profit margin you can expect after taking into account all the numbers in the “Numbers Breakdown', 'profit_margin')}
            </span>
            <Input
              className="numbers-breakdown-card-input"
              name="profit_margin"
              value={`${acc.formatNumber(memoMargin, 2, ',')}%`}
              disabled
            />
          </div>
        </Col>
      </Row>
    </ProductDetailsCard>
  );

};

export default NumbersBreakdownBlock;
